import { useState } from "react";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "suomifi-ui-components";
import {
  IconChartScreen,
  IconHelpdesk,
  IconUserProfile,
  IconChartAnalytics,
  IconLogout,
  IconAuthorisation,
} from "suomifi-icons";
import type { ComponentType } from "react";

import { voiNahda, type Toiminto } from "../utils/oikeudet";
import { SIDE_NAV_WIDTH } from "./constants";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useGetTukipyynnotQuery } from "../api/backendApi";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

/**
 * Sivupalkki — sovelluksen ainoa kromi (yläpalkki poistettu).
 *
 * Pinottu ylhäältä alas:
 *  1. PROTOTYYPPI-varoitusbanneri (CLAUDE.md vaatimus: prototyyppi aina
 *     selvästi erotettavissa tuotannosta)
 *  2. Brand-logo (Finnish Authenticator Admin)
 *  3. Navigaatiolista
 *  4. (spacer)
 *  5. Käyttäjälohko (avatar, nimi, UID)
 *  6. Kirjaudu ulos -tertiary-painike
 *  7. Versio-footer (depthLight2-viiva painikkeen yläpuolella)
 *
 * Sivupalkki on kiinni ikkunan vasemmassa, ylä- ja alareunassa (top=left=
 * bottom=0). Oikealla puolella AppShell:n Main hoitaa 25px gapin sisältöön.
 *
 * Logo on SVG /logos/finauth-admin.svg. Logoista voi tulla mobile/dark-
 * versioita — pidetään ne `public/logos/` -kansiossa.
 */

type IllustrativeIcon = ComponentType<{
  baseColor?: string;
  highlightColor?: string;
  ariaLabel?: string;
  className?: string;
}>;

interface NavItem {
  label: string;
  path: string;
  Icon: IllustrativeIcon;
  disabled?: boolean;
  /** Jos asetettu, item näytetään vain rooleille joilla on tämä oikeus.
   * Etusivu on aina kaikilla (ei toimintoa). */
  toiminto?: Toiminto;
}

const piilotaRaportit = import.meta.env.VITE_PIILOTA_RAPORTIT === "true";

const navItems: NavItem[] = piilotaRaportit
  ? [
      { label: "sivut.etusivu", path: "/", Icon: IconChartScreen },
      /* Avoin-filtteri oletuksena: numeropallo näyttää avoimien lukumäärän,
     joten klikkaus vie täsmälleen niihin (käyttäjän intuitio). Filtteriä
     voi tietysti vaihtaa listalla. */
      {
        label: "sivut.tukipyynnot",
        path: "/tukipyynnot?tila=avoin",
        Icon: IconHelpdesk,
        toiminto: "navi.tukipyynnot",
      },
      {
        label: "sivut.kayttajahaku",
        path: "/kayttajahaku",
        Icon: IconUserProfile,
        toiminto: "navi.kayttajahaku",
      },
      {
        label: "sivut.kayttooikeudet",
        path: "/kayttooikeudet",
        Icon: IconAuthorisation,
        toiminto: "navi.kayttooikeudet",
      },
    ]
  : [
      { label: "sivut.etusivu", path: "/", Icon: IconChartScreen },
      {
        label: "sivut.tukipyynnot",
        path: "/tukipyynnot?tila=avoin",
        Icon: IconHelpdesk,
        toiminto: "navi.tukipyynnot",
      },
      {
        label: "sivut.kayttajahaku",
        path: "/kayttajahaku",
        Icon: IconUserProfile,
        toiminto: "navi.kayttajahaku",
      },
      {
        label: "sivut.raportit",
        path: "/raportit",
        Icon: IconChartAnalytics,
        toiminto: "navi.raportit",
      },
      {
        label: "sivut.kayttooikeudet",
        path: "/kayttooikeudet",
        Icon: IconAuthorisation,
        toiminto: "navi.kayttooikeudet",
      },
    ];

const NavRoot = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${SIDE_NAV_WIDTH};
  background: ${({ theme }) => theme.colors.whiteBase};
  border-right: 1px solid ${({ theme }) => theme.colors.depthLight1};
  overflow-y: auto;
  z-index: 40;
  display: flex;
  flex-direction: column;
`;

const ProtoBanner = styled.div`
  background: ${({ theme }) => theme.colors.warningBase};
  color: ${({ theme }) => theme.colors.blackBase};
  text-align: center;
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.625rem;
  letter-spacing: 0.02em;
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xs};
  line-height: 1.3;
  white-space: nowrap;
`;

const Brand = styled.div`
  padding: ${({ theme }) => theme.spacing.m} ${({ theme }) => theme.spacing.s} 0;
`;

const BrandLogo = styled.img`
  display: block;
  height: auto;
  width: auto;
  max-width: 100%;
`;

const NavList = styled.div`
  padding: ${({ theme }) => theme.spacing.l} 0 ${({ theme }) => theme.spacing.s};
  flex: 1;
`;

interface NavButtonProps {
  $active: boolean;
  $disabled: boolean;
}

const NavButton = styled.button<NavButtonProps>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s};
  border: 0;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.highlightLight3 : "transparent"};
  color: ${({ theme, $active, $disabled }) => {
    if ($disabled) return theme.colors.depthBase;
    if ($active) return theme.colors.brandBase;
    return theme.colors.blackBase;
  }};
  font: ${({ theme, $active }) =>
    $active
      ? theme.typography.bodySemiBoldSmall
      : theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  border-left: ${({ theme, $active }) =>
    $active ? `4px solid ${theme.colors.brandBase}` : "4px solid transparent"};
  padding-left: calc(${({ theme }) => theme.spacing.s} - 4px);
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme, $disabled, $active }) => {
      if ($disabled) return "transparent";
      if ($active) return theme.colors.highlightLight3;
      return theme.colors.depthLight3;
    }};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: -2px;
  }
`;

const IconWrap = styled.span`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & > svg {
    width: 2rem;
    height: 2rem;
  }
`;

const NavLabel = styled.span`
  flex: 1;
`;

const Counter = styled.span`
  background: ${({ theme }) => theme.colors.alertBase};
  color: ${({ theme }) => theme.colors.whiteBase};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  border-radius: 999px;
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xxs};
  min-width: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const ComingTag = styled.span`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthBase};
  font-style: italic;
`;

const UserBlock = styled.div`
  padding: ${({ theme }) => theme.spacing.m} ${({ theme }) => theme.spacing.s};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Avatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.brandBase};
  color: ${({ theme }) => theme.colors.whiteBase};
  display: flex;
  align-items: center;
  justify-content: center;
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  flex-shrink: 0;
`;

const UserMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.blackBase};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserRole = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const UserUid = styled.div`
  font-family: "Courier New", monospace;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

/* Kirjaudu ulos — tertiary-painike (secondaryNoBorder), IconLogout oikealla.
   Klikkaus on no-op prototyypissä; uloskirjautumislogiikkaa ei toteuteta. */
const LogoutWrap = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};

  & .fi-button {
    min-height: 32px;
    padding: 4px 10px;
    font-size: 0.8125rem;
  }
  & .fi-button .fi-icon {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

/* Viiva versionumeron yläpuolella ei ulotu sivupalkin reunoihin —
   sisennys 15px (s-token) molemmin puolin. */
/* Klikattava virkailija/rooli-blokki sivupalkin alalaidassa.
   Klikkaus avaa profiilinvalitsin-popovert ylöspäin. */
const RooliBlokki = styled.button`
  width: 100%;
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.s};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};

  &:hover {
    background: ${({ theme }) => theme.colors.highlightLight4};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: -2px;
  }

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
    margin-left: auto;
  }
`;

/* Popover-lista profiilivaihtoehdoille — absoluttinen, avautuu ylöspäin. */
const ProfiiliPopover = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  box-shadow: 0 -4px 16px hsla(0, 0%, 0%, 0.1);
  z-index: 200;
  max-height: 60vh;
  overflow-y: auto;
`;

const ProfiiliValinta = styled.button<{ $aktiivinen: boolean }>`
  width: 100%;
  background: ${({ theme, $aktiivinen }) =>
    $aktiivinen ? theme.colors.highlightLight4 : "transparent"};
  border: 0;
  border-left: 3px solid
    ${({ theme, $aktiivinen }) =>
      $aktiivinen ? theme.colors.brandBase : "transparent"};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.insetXxs};

  &:hover {
    background: ${({ theme }) => theme.colors.highlightLight4};
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.l};
  max-width: 26rem;
  width: calc(100% - 2rem);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const ModalOtsikko = styled.h2`
  margin: 0;
  font: ${({ theme }) => theme.typography.heading3};
  color: ${({ theme }) => theme.colors.blackBase};
`;

const ModalTeksti = styled.p`
  margin: 0;
  font: ${({ theme }) => theme.typography.bodyText};
  color: ${({ theme }) => theme.colors.blackBase};
`;

const NavLinkStyled = styled(NavLink)<{ $active: boolean; $disabled: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s};
  border: 0;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.highlightLight3 : "transparent"};
  color: ${({ theme, $active, $disabled }) => {
    if ($disabled) return theme.colors.depthBase;
    if ($active) return theme.colors.brandBase;
    return theme.colors.blackBase;
  }};
  font: ${({ theme, $active }) =>
    $active
      ? theme.typography.bodySemiBoldSmall
      : theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  border-left: ${({ theme, $active }) =>
    $active ? `4px solid ${theme.colors.brandBase}` : "4px solid transparent"};
  padding-left: calc(${({ theme }) => theme.spacing.s} - 4px);
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme, $disabled, $active }) => {
      if ($disabled) return "transparent";
      if ($active) return theme.colors.highlightLight3;
      return theme.colors.depthLight3;
    }};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: -2px;
  }
`;
const ModalNapit = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.s};
`;

const ProfiiliNimi = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const ProfiiliKuvaus = styled.span`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

/* Wrapper tekee position: relative mahdolliseksi popovert:lle */
const RooliWrap = styled.div`
  position: relative;
`;

const FooterDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.depthLight2};
  margin: 0 ${({ theme }) => theme.spacing.s};
`;

const FooterVersion = styled.div`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.depthBase};
`;

export default function SideNav() {
  const { t, i18n } = useTranslation(["sideNav", "common"]);
  const location = useLocation();
  const navigate = useNavigate();

  const { data: tukipyynnot = [] } = useGetTukipyynnotQuery();
  const avoimetCount = tukipyynnot.filter((t) => t.tila === "avoin").length;
  /* Suodatetaan navi-itemit roolin perusteella. Etusivu (ei toimintoa) on
     aina kaikilla. Muut näytetään vain jos roolilla on oikeus. */

  const { kayttaja, todellinenRooli, isLoading, error } = useTodellinenRooli();

  const initials =
    (kayttaja?.name.givenName?.[0] ?? "") +
    (kayttaja?.name.familyName?.[0] ?? "");

  const naytettavatNavItems = todellinenRooli
    ? navItems.filter((item) =>
        item.toiminto
          ? voiNahda(item.toiminto, todellinenRooli.profiili)
          : true,
      )
    : [];

  const [logoutConfirmAuki, setLogoutConfirmAuki] = useState(false);
  useModaaliBackdropSulku(logoutConfirmAuki, () => setLogoutConfirmAuki(false));

  return (
    <NavRoot>
      <Brand>
        <BrandLogo
          src="/logos/finauth-admin.svg"
          alt="Finnish Authenticator Admin"
        />
      </Brand>
      <NavList>
        {naytettavatNavItems.map((item) => {
          // item.path voi sisältää query-stringin (esim. ?tila=avoin) —
          // vertailu vain pathname-osaan, ei query-osaan.
          const itemPathname = item.path.split("?")[0];
          const isActive =
            itemPathname === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(itemPathname);
          const isDisabled = !!item.disabled;

          return (
            <NavLinkStyled
              key={item.path}
              to={item.path}
              $active={isActive}
              $disabled={isDisabled}
            >
              <IconWrap>
                <item.Icon
                  baseColor={
                    isDisabled
                      ? "#A4ABAF" /* depthBase */
                      : "#003479" /* brandBase */
                  }
                  highlightColor={
                    isDisabled
                      ? "#C9CDD0" /* depthLight1 */
                      : "#E97917" /* accentBase */
                  }
                  ariaLabel=""
                />
              </IconWrap>
              <NavLabel>
                {t(item.label, {
                  ns: "common",
                })}
              </NavLabel>
              {item.label === "sivut.tukipyynnot" && avoimetCount > 0 && (
                <Counter>{avoimetCount}</Counter>
              )}
              {isDisabled && <ComingTag>tulossa</ComingTag>}
            </NavLinkStyled>
          );
        })}
      </NavList>
      <UserBlock>
        <UserRow>
          <Avatar>{initials}</Avatar>
          <UserMeta>
            <UserName>
              {kayttaja?.name?.givenName} {kayttaja?.name?.familyName}
            </UserName>
            <UserUid>{kayttaja?.customUid}</UserUid>
            <UserRole>{todellinenRooli?.nimi ?? "Mukautettu"}</UserRole>
          </UserMeta>
        </UserRow>
      </UserBlock>
      <LogoutWrap>
        <Button
          variant="secondaryNoBorder"
          fullWidth
          iconRight={<IconLogout />}
          onClick={() => setLogoutConfirmAuki(true)}
        >
          {t("logout.teksti")}
        </Button>
      </LogoutWrap>
      {logoutConfirmAuki && (
        <ModalBackdrop
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-otsikko"
        >
          <ModalBox>
            <ModalOtsikko id="logout-otsikko">
              {t("logout.teksti")}?
            </ModalOtsikko>
            <ModalTeksti>{t("logout.varmistus")}</ModalTeksti>
            <ModalNapit>
              <Button
                variant="secondary"
                onClick={() => setLogoutConfirmAuki(false)}
              >
                {t("logout.peruuta")}
              </Button>
              <Button
                onClick={() => {
                  // Redirect to backend logout only after explicit confirmation
                  window.location.href = "/api/logout";
                }}
              >
                {t("logout.teksti")}
              </Button>
            </ModalNapit>
          </ModalBox>
        </ModalBackdrop>
      )}
      <FooterDivider />
      <LanguageSwitcher />
      <FooterVersion>v0.994 · Release Candidate</FooterVersion>
    </NavRoot>
  );
}
